<template>
  <div class="min-h-screen flex flex-col bg-warm-cream">
    <!-- SEO Meta Tags -->
    <Head>
      <title v-if="title">{{ title }} - RealGestión</title>
      <meta name="description" :content="description || 'RealGestión - La solución integral para la gestión de tu negocio. Administra clientes, productos, inventarios, compras y ventas de forma eficiente.'" />
      <meta name="keywords" content="gestión de negocios, control de inventario, gestión de clientes, sistema de ventas, facturación, administración de negocios, condominios, ecommerce" />
      
      <!-- Open Graph / Facebook -->
      <meta property="og:type" content="website" />
      <meta property="og:url" :content="url || 'https://realgestion.lat'" />
      <meta property="og:title" :content="title || 'RealGestión - Gestiona tu negocio de forma eficiente'" />
      <meta property="og:description" :content="description || 'La solución integral para la gestión de tu negocio.'" />
      <meta property="og:image" :content="image || 'https://realgestion.lat/images/og-image.jpg'" />

      <!-- Twitter -->
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" :content="url || 'https://realgestion.lat'" />
      <meta property="twitter:title" :content="title || 'RealGestión'" />
      <meta property="twitter:description" :content="description || 'La solución integral para la gestión de tu negocio.'" />
      <meta property="twitter:image" :content="image || 'https://realgestion.lat/images/og-image.jpg'" />

      <!-- Canonical URL -->
      <link rel="canonical" :href="url || 'https://realgestion.lat'" />

      <!-- Favicon -->
      <link rel="icon" type="image/png" href="/favicon.png" />
      <link rel="apple-touch-icon" href="/apple-touch-icon.png" />

      <!-- Additional Meta Tags -->
      <meta name="robots" content="index, follow" />
      <meta name="language" content="Spanish" />
      <meta name="revisit-after" content="7 days" />
      <meta name="author" content="RealGestión" />

      <!-- Google Fonts: Inter -->
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />
    </Head>

    <!-- Header -->
    <header 
      class="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      :class="[
        scrolled ? 'header-scrolled' : 'bg-transparent'
      ]"
    >
      <nav class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center nav-height">
          <!-- Logo -->
          <div class="flex-shrink-0">
            <Link href="/" class="flex items-center">
              <Logo class="h-8 w-auto" />
            </Link>
          </div>

          <!-- Desktop Nav -->
          <div class="hidden lg:flex items-center gap-1">
            <!-- Soluciones Dropdown -->
            <div class="relative" @mouseenter="openDropdown('soluciones')" @mouseleave="closeDropdown">
              <button 
                class="nav-link group flex items-center gap-1.5 px-4 py-2 nav-text font-medium transition-colors rounded-lg"
                :class="[activeDropdown === 'soluciones' ? 'text-orange-600' : 'text-gray-700 hover:text-orange-600']"
              >
                Soluciones
                <svg class="w-3.5 h-3.5 transition-transform duration-300" :class="{'rotate-180': activeDropdown === 'soluciones'}" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
              </button>

              <!-- Soluciones Mega Menu -->
              <transition
                enter-active-class="transition duration-200 ease-out"
                enter-from-class="opacity-0 translate-y-2 scale-95"
                enter-to-class="opacity-100 translate-y-0 scale-100"
                leave-active-class="transition duration-150 ease-in"
                leave-from-class="opacity-100 translate-y-0 scale-100"
                leave-to-class="opacity-0 translate-y-2 scale-95"
              >
                <div v-show="activeDropdown === 'soluciones'" class="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 dropdown-w-lg bg-white rounded-2xl dropdown-shadow border border-gray-100 overflow-hidden">
                  <div class="p-2">
                    <div class="px-4 pt-3 pb-2">
                      <span class="text-xs font-semibold text-gray-400 uppercase tracking-widest">Administrativo</span>
                    </div>
                    <a
                      v-for="item in adminMenuItems" :key="item.title" :href="item.href"
                      class="flex items-center gap-3.5 px-4 py-3 rounded-xl hover:bg-orange-50/60 transition-colors group"
                    >
                      <div class="flex-shrink-0 w-10 h-10 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center group-hover:bg-orange-100 group-hover:border-orange-200 transition-colors">
                        <i :class="item.icon + ' text-orange-500 text-sm'" />
                      </div>
                      <div>
                        <div class="text-sm font-semibold text-gray-800 group-hover:text-orange-600 transition-colors">{{ item.title }}</div>
                        <div class="text-xs text-gray-400 leading-tight">{{ item.subtitle }}</div>
                      </div>
                    </a>
                  </div>
                </div>
              </transition>
            </div>

            <!-- Condominios Dropdown -->
            <div class="relative" @mouseenter="openDropdown('condominios')" @mouseleave="closeDropdown">
              <button 
                class="nav-link group flex items-center gap-1.5 px-4 py-2 nav-text font-medium transition-colors rounded-lg"
                :class="[activeDropdown === 'condominios' ? 'text-emerald-600' : 'text-gray-700 hover:text-emerald-600']"
              >
                Condominios
                <svg class="w-3.5 h-3.5 transition-transform duration-300" :class="{'rotate-180': activeDropdown === 'condominios'}" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
              </button>

              <!-- Condominios Mega Menu -->
              <transition
                enter-active-class="transition duration-200 ease-out"
                enter-from-class="opacity-0 translate-y-2 scale-95"
                enter-to-class="opacity-100 translate-y-0 scale-100"
                leave-active-class="transition duration-150 ease-in"
                leave-from-class="opacity-100 translate-y-0 scale-100"
                leave-to-class="opacity-0 translate-y-2 scale-95"
              >
                <div v-show="activeDropdown === 'condominios'" class="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 dropdown-w bg-white rounded-2xl dropdown-shadow border border-gray-100 overflow-hidden">
                  <div class="p-2">
                    <div class="px-4 pt-3 pb-2">
                      <span class="text-xs font-semibold text-gray-400 uppercase tracking-widest">Condominios</span>
                    </div>
                    <a
                      v-for="item in condoMenuItems" :key="item.title" :href="item.href"
                      class="flex items-center gap-3.5 px-4 py-3 rounded-xl hover:bg-emerald-50/60 transition-colors group"
                    >
                      <div class="flex-shrink-0 w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center group-hover:bg-emerald-100 group-hover:border-emerald-200 transition-colors">
                        <i :class="item.icon + ' text-emerald-500 text-sm'" />
                      </div>
                      <div>
                        <div class="text-sm font-semibold text-gray-800 group-hover:text-emerald-600 transition-colors">{{ item.title }}</div>
                        <div class="text-xs text-gray-400 leading-tight">{{ item.subtitle }}</div>
                      </div>
                    </a>
                  </div>
                </div>
              </transition>
            </div>

            <!-- Ecommerce Link -->
            <a href="#ecommerce" class="nav-link px-4 py-2 nav-text font-medium text-gray-700 hover:text-blue-600 transition-colors rounded-lg">
              Ecommerce
            </a>

            <!-- Precios -->
            <Link href="/pricing" class="nav-link px-4 py-2 nav-text font-medium text-gray-700 hover:text-orange-600 transition-colors rounded-lg">
              Precios
            </Link>
          </div>

          <!-- Desktop CTA -->
          <div class="hidden lg:flex items-center gap-3">
            <Link href="/login" class="px-5 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
              Iniciar sesión
            </Link>
            <Link href="/register" class="px-6 py-2.5 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 rounded-full transition-all duration-300 hover:shadow-lg nav-cta-hover">
              Comenzar gratis
            </Link>
          </div>

          <!-- Mobile Menu Button -->
          <button class="lg:hidden p-2 rounded-xl hover:bg-gray-100 transition-colors" aria-label="Menú" @click="mobileOpen = !mobileOpen">
            <svg v-if="!mobileOpen" class="w-6 h-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 6h16M4 12h16M4 18h16" /></svg>
            <svg v-else class="w-6 h-6 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      </nav>

      <!-- Mobile Menu -->
      <transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 -translate-y-4"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition duration-200 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-4"
      >
        <div v-show="mobileOpen" class="lg:hidden bg-white border-t border-gray-100 shadow-xl mobile-menu-height overflow-y-auto">
          <div class="px-4 py-4 space-y-1">
            <!-- Mobile: Soluciones Accordion -->
            <div>
              <button
                class="w-full flex items-center justify-between px-4 py-3 nav-text font-medium text-gray-700 rounded-xl hover:bg-gray-50" 
                @click="mobileAccordion = mobileAccordion === 'soluciones' ? null : 'soluciones'"
              >
                <span class="flex items-center gap-2"><i class="fas fa-building text-orange-500 text-sm" /> Soluciones</span>
                <svg class="w-4 h-4 transition-transform" :class="{'rotate-180': mobileAccordion === 'soluciones'}" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
              </button>
              <div v-show="mobileAccordion === 'soluciones'" class="pl-4 pb-2 space-y-1">
                <a
                  v-for="item in adminMenuItems" :key="item.title" :href="item.href" class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:text-orange-600 rounded-lg hover:bg-orange-50/50 transition-colors"
                  @click="mobileOpen = false"
                >
                  <i :class="item.icon + ' text-orange-400 text-xs w-4 text-center'" />
                  {{ item.title }}
                </a>
              </div>
            </div>

            <!-- Mobile: Condominios Accordion -->
            <div>
              <button
                class="w-full flex items-center justify-between px-4 py-3 nav-text font-medium text-gray-700 rounded-xl hover:bg-gray-50" 
                @click="mobileAccordion = mobileAccordion === 'condominios' ? null : 'condominios'"
              >
                <span class="flex items-center gap-2"><i class="fas fa-city text-emerald-500 text-sm" /> Condominios</span>
                <svg class="w-4 h-4 transition-transform" :class="{'rotate-180': mobileAccordion === 'condominios'}" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
              </button>
              <div v-show="mobileAccordion === 'condominios'" class="pl-4 pb-2 space-y-1">
                <a
                  v-for="item in condoMenuItems" :key="item.title" :href="item.href" class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-600 hover:text-emerald-600 rounded-lg hover:bg-emerald-50/50 transition-colors"
                  @click="mobileOpen = false"
                >
                  <i :class="item.icon + ' text-emerald-400 text-xs w-4 text-center'" />
                  {{ item.title }}
                </a>
              </div>
            </div>

            <a href="#ecommerce" class="flex items-center gap-2 px-4 py-3 nav-text font-medium text-gray-700 rounded-xl hover:bg-gray-50" @click="mobileOpen = false">
              <i class="fas fa-store text-blue-500 text-sm" /> Ecommerce
            </a>
            <Link href="/pricing" class="flex items-center gap-2 px-4 py-3 nav-text font-medium text-gray-700 rounded-xl hover:bg-gray-50" @click="mobileOpen = false">
              <i class="fas fa-tags text-gray-400 text-sm" /> Precios
            </Link>

            <div class="pt-4 border-t border-gray-100 space-y-2">
              <Link href="/login" class="block text-center px-4 py-3 text-sm font-medium text-gray-600 hover:text-gray-900 rounded-xl hover:bg-gray-50 transition-colors">
                Iniciar sesión
              </Link>
              <Link href="/register" class="block text-center px-4 py-3 text-sm font-semibold text-white bg-gray-900 rounded-xl hover:bg-gray-800 transition-colors">
                Comenzar gratis
              </Link>
            </div>
          </div>
        </div>
      </transition>
    </header>

    <!-- Main Content -->
    <main class="flex-grow">
      <slot />
    </main>

    <!-- Footer -->
    <footer class="bg-gray-950 text-gray-300">
      <!-- Top Section -->
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12">
        <div class="grid grid-cols-2 md:grid-cols-5 gap-8 lg:gap-12">
          <!-- Brand -->
          <div class="col-span-2 md:col-span-2">
            <Link href="/" class="inline-block mb-6">
              <Logo class="h-8 w-auto brightness-0 invert" />
            </Link>
            <p class="text-gray-400 text-sm leading-relaxed max-w-xs mb-6">
              La solución integral para la gestión de tu negocio. Administra clientes, productos, inventarios, compras y ventas de forma eficiente.
            </p>
            <div class="flex items-center gap-4">
              <a target="_blank" href="https://www.instagram.com/realgestionve" class="w-10 h-10 rounded-full bg-gray-800 hover:bg-orange-500 flex items-center justify-center transition-colors duration-300">
                <i class="fab fa-instagram text-sm" />
              </a>
              <a target="_blank" href="https://wa.me/584244010195" class="w-10 h-10 rounded-full bg-gray-800 hover:bg-orange-500 flex items-center justify-center transition-colors duration-300">
                <i class="fab fa-whatsapp text-sm" />
              </a>
            </div>
          </div>

          <!-- Producto -->
          <div>
            <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-4">Producto</h3>
            <ul class="space-y-3">
              <li><a href="#administrativo" class="text-sm text-gray-400 hover:text-white transition-colors">Administrativo</a></li>
              <li><a href="#condominios" class="text-sm text-gray-400 hover:text-white transition-colors">Condominios</a></li>
              <li><a href="#ecommerce" class="text-sm text-gray-400 hover:text-white transition-colors">Ecommerce</a></li>
              <li><a href="#features" class="text-sm text-gray-400 hover:text-white transition-colors">Funcionalidades</a></li>
            </ul>
          </div>

          <!-- Empresa -->
          <div>
            <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-4">Empresa</h3>
            <ul class="space-y-3">
              <li><Link href="/pricing" class="text-sm text-gray-400 hover:text-white transition-colors">Precios</Link></li>
              <li><a href="#testimonials" class="text-sm text-gray-400 hover:text-white transition-colors">Testimonios</a></li>
            </ul>
          </div>

          <!-- Legal -->
          <div>
            <h3 class="text-xs font-semibold text-gray-500 uppercase tracking-widest mb-4">Legal</h3>
            <ul class="space-y-3">
              <li><Link href="/terms" class="text-sm text-gray-400 hover:text-white transition-colors">Términos de uso</Link></li>
              <li><Link href="/privacy" class="text-sm text-gray-400 hover:text-white transition-colors">Privacidad</Link></li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Bottom Bar -->
      <div class="border-t border-gray-800">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p class="text-xs text-gray-500">
            &copy; {{ new Date().getFullYear() }} Realgestión. Todos los derechos reservados.
          </p>
          <p class="text-xs text-gray-600">
            Hecho con <span class="text-orange-500">♥</span> en Venezuela
          </p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { Link, Head } from '@inertiajs/inertia-vue3'
import Logo from '@/Shared/Logo'

export default {
  components: {
    Link,
    Head,
    Logo,
  },
  props: {
    title: { type: String, default: null },
    description: { type: String, default: null },
    url: { type: String, default: null },
    image: { type: String, default: null },
  },
  data() {
    return {
      scrolled: false,
      mobileOpen: false,
      mobileAccordion: null,
      activeDropdown: null,
      dropdownTimeout: null,
      adminMenuItems: [
        { title: 'Gestión de Clientes', subtitle: 'Cartera, créditos y pagos', icon: 'fas fa-users', href: '#administrativo' },
        { title: 'Gestión de Productos', subtitle: 'Catálogo y precios', icon: 'fas fa-box-open', href: '#administrativo' },
        { title: 'Gestión de Inventarios', subtitle: 'Multidepósito y trazabilidad', icon: 'fas fa-warehouse', href: '#administrativo' },
        { title: 'Gestión de Ventas', subtitle: 'POS, cotizaciones y facturación', icon: 'fas fa-cash-register', href: '#administrativo' },
        { title: 'Gestión de Compras', subtitle: 'Proveedores y cuentas por pagar', icon: 'fas fa-truck-loading', href: '#administrativo' },
        { title: 'Gestión de Gastos', subtitle: 'Egresos y utilidad neta', icon: 'fas fa-receipt', href: '#administrativo' },
        { title: 'Mi Catálogo', subtitle: 'Tienda digital con WhatsApp', icon: 'fas fa-shopping-bag', href: '#ecommerce' },
      ],
      condoMenuItems: [
        { title: 'Gestión de Propietarios', subtitle: 'Directorio y unidades', icon: 'fas fa-user-friends', href: '#condominios' },
        { title: 'Gestión de Cobranza', subtitle: 'Cuotas y morosidad', icon: 'fas fa-file-invoice-dollar', href: '#condominios' },
        { title: 'Gastos y Alícuotas', subtitle: 'Cálculos proporcionales', icon: 'fas fa-calculator', href: '#condominios' },
        { title: 'Reportes', subtitle: 'Balance y estados financieros', icon: 'fas fa-chart-bar', href: '#condominios' },
        { title: 'Portal del Propietario', subtitle: 'Autogestión 24/7', icon: 'fas fa-home', href: '#condominios' },
      ],
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
    this.handleScroll()
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    handleScroll() {
      this.scrolled = window.scrollY > 20
    },
    openDropdown(name) {
      clearTimeout(this.dropdownTimeout)
      this.activeDropdown = name
    },
    closeDropdown() {
      this.dropdownTimeout = setTimeout(() => {
        this.activeDropdown = null
      }, 150)
    },
  },
}
</script>

<style scoped>
.font-display {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}
.bg-warm-cream { background-color: #FAFAF7; }
.nav-height { height: 72px; }
.nav-text { font-size: 15px; }
.nav-cta-hover:hover { transform: translateY(-1px); }
.dropdown-w { width: 400px; }
.dropdown-w-lg { width: 420px; }
.dropdown-shadow { box-shadow: 0 20px 60px -12px rgba(0,0,0,0.15); }
.header-scrolled {
  background: rgba(255,255,255,0.9);
  -webkit-backdrop-filter: blur(24px);
  backdrop-filter: blur(24px);
  box-shadow: 0 1px 0 rgba(0,0,0,0.06);
}
.mobile-menu-height { max-height: calc(100vh - 72px); }
</style>